import styles from "./CareManagerDashboard.module.css";
import { FaCalendarAlt, FaUserPlus } from "react-icons/fa";
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

const formatAssignedAgo = (assignedAt) => {
  if (!assignedAt) return "";

  const assignedDate = new Date(assignedAt);
  if (Number.isNaN(assignedDate.getTime())) return "";

  const distance = formatDistanceToNowStrict(assignedDate, { addSuffix: false });

  if (distance.endsWith(" second") || distance.endsWith(" seconds")) {
    return "Just now";
  }

  if (distance.endsWith(" minute") || distance.endsWith(" minutes")) {
    const minutes = parseInt(distance, 10);
    return minutes <= 1 ? "1m ago" : `${minutes}m ago`;
  }

  if (distance.endsWith(" hour") || distance.endsWith(" hours")) {
    const hours = parseInt(distance, 10);
    return `${hours}h ago`;
  }

  if (distance.endsWith(" day") || distance.endsWith(" days")) {
    const days = parseInt(distance, 10);
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }

  return `${distance} ago`;
};

const CareManagerDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);
  const authToken = user?.user?.token;

  const [isLoading, setIsLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [newAssignments, setNewAssignments] = useState([]);
  const [assignmentCount, setAssignmentCount] = useState(0);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/care-manager-dashboard?timezone=${encodeURIComponent(timezone)}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data?.success) {
          setTodayAppointments(response.data.data?.todayAppointments || []);
          setNewAssignments(response.data.data?.newAssignments || []);
          setAssignmentCount(response.data.data?.assignmentCount || 0);
        }
      } catch (error) {
        console.error("Error fetching care manager dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [authToken]);

  return (
    <div className={`app-page ${styles.pageShell}`}>
      <div className={styles.pageCard}>
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Dashboard</h2>
        </div>

        <div className={styles.widgetsSection}>
          <div className={styles.widgetsRow}>
            <section className={styles.widget}>
              <div className={styles.widgetHeader}>
                <FaCalendarAlt className={styles.widgetIcon} />
                <h2 className={styles.widgetTitle}>
                  Today&apos;s Upcoming Appointments
                </h2>
              </div>

              <div className={styles.widgetBody}>
                {isLoading ? (
                  <p className={styles.loadingState}>Loading appointments...</p>
                ) : todayAppointments.length > 0 ? (
                  todayAppointments.map((appointment) => (
                    <div className={styles.card} key={appointment.id}>
                      <p className={styles.patientName}>
                        {appointment.patientName}
                      </p>
                      <p className={styles.cardMeta}>
                        {appointment.appointmentTime}
                        {appointment.clinicName
                          ? ` · ${appointment.clinicName}`
                          : ""}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className={styles.emptyState}>
                    No appointments scheduled today.
                  </p>
                )}
              </div>

              <div className={styles.widgetFooter}>
                <button
                  className={styles.ctaLink}
                  onClick={() => navigate("/appointments")}
                  type="button"
                >
                  View Full Schedule
                  <span className={styles.ctaArrow}>→</span>
                </button>
              </div>
            </section>

            <section className={styles.widget}>
              <div className={styles.widgetHeaderWithBadge}>
                <div className={styles.widgetTitleGroup}>
                  <FaUserPlus className={styles.widgetIcon} />
                  <h2 className={styles.widgetTitle}>New Patient Assignments</h2>
                </div>
                {!isLoading && assignmentCount > 0 && (
                  <span className={styles.badge}>{assignmentCount}</span>
                )}
              </div>

              <div className={styles.widgetBody}>
                {isLoading ? (
                  <p className={styles.loadingState}>Loading assignments...</p>
                ) : newAssignments.length > 0 ? (
                  newAssignments.map((assignment) => (
                    <div className={styles.card} key={assignment.id}>
                      <p className={styles.patientName}>
                        {assignment.patientName}
                      </p>
                      <p className={styles.cardMeta}>
                        {assignment.clinicName}
                        {assignment.assignedAt
                          ? ` · ${formatAssignedAgo(assignment.assignedAt)}`
                          : ""}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className={styles.emptyState}>No new patient assignments.</p>
                )}
              </div>

              <div className={styles.widgetFooter}>
                <button
                  className={styles.ctaLink}
                  onClick={() => navigate("/careManager/my-patients")}
                  type="button"
                >
                  View All Patients
                  <span className={styles.ctaArrow}>→</span>
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareManagerDashboard;
